import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { AlertCircle } from "lucide-react";
import storageLocationService from "@/services/storageLocationService";
import equipmentService from "@/services/equipmentService";

/**
 * Modal de formulário para criar/editar local de armazenamento
 */
export default function StorageLocationFormModal({
  open,
  onOpenChange,
  storageLocation,
  onSave,
  loading,
}) {
  const [formData, setFormData] = useState({
    name: "",
    type: "",
    capacity: "",
  });

  const [errors, setErrors] = useState({});
  const [equipments, setEquipments] = useState([]);
  const [loadingEquipments, setLoadingEquipments] = useState(true);

  // Buscar equipamentos da API
  useEffect(() => {
    const fetchEquipments = async () => {
      try {
        setLoadingEquipments(true);
        const data = await equipmentService.getAllEquipments();
        setEquipments(Array.isArray(data) ? data : []);
      } catch (error) {
        console.error("Erro ao buscar equipamentos:", error);
        setEquipments([]);
      } finally {
        setLoadingEquipments(false);
      }
    };

    if (open) {
      fetchEquipments();
    }
  }, [open]);

  useEffect(() => {
    if (storageLocation) {
      // Editando local existente
      const equipmentId = String(
        storageLocation.equipment_id ||
          storageLocation.equipmentId ||
          storageLocation.type ||
          ""
      );

      setFormData({
        name: storageLocation.name || "",
        type: equipmentId,
        capacity: storageLocation.capacity || "",
      });
    } else {
      // Novo local
      resetForm();
    }
    setErrors({});
  }, [storageLocation, open]);

  const resetForm = () => {
    setFormData({
      name: "",
      type: "",
      capacity: "",
    });
    setErrors({});
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.name || formData.name.trim() === "") {
      newErrors.name = "Nome é obrigatório";
    }

    // Validação do equipamento (aceita string ou número)
    if (!formData.type || String(formData.type).trim() === "") {
      newErrors.type = "Equipamento é obrigatório";
    }

    if (!formData.capacity || parseFloat(formData.capacity) <= 0) {
      newErrors.capacity = "Capacidade deve ser maior que zero";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    const locationData = {
      name: formData.name.trim(),
      equipment_id: formData.type,
      capacity: parseInt(formData.capacity),
    };

    onSave(locationData);
  };

  const handleChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    // Limpar erro do campo quando o usuário começar a digitar
    if (errors[field]) {
      setErrors((prev) => ({ ...prev, [field]: null }));
    }
  };

  // Mapear equipamentos para options (usando name como label e id como value)
  const equipmentOptions = equipments.map((equipment) => ({
    value: String(equipment.id), // Garantir que o value seja string
    label: equipment.name,
  }));

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>
            {storageLocation
              ? "Editar Local de Armazenamento"
              : "Novo Local de Armazenamento"}
          </DialogTitle>
          <DialogDescription>
            {storageLocation
              ? "Atualize as informações do local de armazenamento"
              : "Preencha os dados para criar um novo local de armazenamento"}
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Nome */}
          <div className="space-y-2">
            <Label htmlFor="name">
              Nome <span className="text-red-500">*</span>
            </Label>
            <Input
              id="name"
              type="text"
              placeholder="Ex: Prateleira A1, Freezer Principal"
              value={formData.name}
              onChange={(e) => handleChange("name", e.target.value)}
              className={errors.name ? "border-red-500" : ""}
            />
            {errors.name && (
              <p className="text-sm text-red-500 flex items-center gap-1">
                <AlertCircle className="w-4 h-4" />
                {errors.name}
              </p>
            )}
          </div>

          {/* Grid para Tipo e Capacidade */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {/* Tipo */}
            <div className="space-y-2">
              <Label htmlFor="type">
                Equipamento <span className="text-red-500">*</span>
              </Label>
              <Select
                value={formData.type}
                onValueChange={(value) => handleChange("type", value)}
                disabled={loadingEquipments}
              >
                <SelectTrigger
                  id="type"
                  className={errors.type ? "border-red-500" : ""}
                >
                  <SelectValue
                    placeholder={
                      loadingEquipments
                        ? "Carregando equipamentos..."
                        : equipmentOptions.length === 0
                        ? "Nenhum equipamento cadastrado"
                        : "Selecione o equipamento"
                    }
                  />
                </SelectTrigger>
                <SelectContent>
                  {equipmentOptions.length === 0 ? (
                    <div className="px-2 py-1.5 text-sm text-gray-500">
                      Nenhum equipamento disponível
                    </div>
                  ) : (
                    equipmentOptions.map((equipment) => (
                      <SelectItem key={equipment.value} value={equipment.value}>
                        {equipment.label}
                      </SelectItem>
                    ))
                  )}
                </SelectContent>
              </Select>
              {errors.type && (
                <p className="text-sm text-red-500 flex items-center gap-1">
                  <AlertCircle className="w-4 h-4" />
                  {errors.type}
                </p>
              )}
              <p className="text-xs text-gray-500">
                Selecione o equipamento para este local
              </p>
            </div>

            {/* Capacidade */}
            <div className="space-y-2">
              <Label htmlFor="capacity">
                Capacidade <span className="text-red-500">*</span>
              </Label>
              <Input
                id="capacity"
                type="number"
                min="1"
                placeholder="100"
                value={formData.capacity}
                onChange={(e) => handleChange("capacity", e.target.value)}
                className={errors.capacity ? "border-red-500" : ""}
              />
              {errors.capacity && (
                <p className="text-sm text-red-500 flex items-center gap-1">
                  <AlertCircle className="w-4 h-4" />
                  {errors.capacity}
                </p>
              )}
              <p className="text-xs text-gray-500">
                Capacidade total em unidades
              </p>
            </div>
          </div>

          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
              disabled={loading}
            >
              Cancelar
            </Button>
            <Button
              type="submit"
              disabled={loading}
              style={{ background: "#c9332C" }}
              onMouseEnter={(e) =>
                !loading && (e.target.style.background = "#a02820")
              }
              onMouseLeave={(e) =>
                !loading && (e.target.style.background = "#c9332C")
              }
            >
              {loading
                ? "Salvando..."
                : storageLocation
                ? "Atualizar Local"
                : "Criar Local"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
